import React, { useState, useEffect, useMemo } from "react";
import {
  Clock,
  CheckCircle,
  Send,
  ArrowLeft,
  ClipboardList,
  FileSpreadsheet,
  ShieldCheck,
  CircleAlert,
  ClipboardCheck,
  ImageIcon,
  XCircle,
  AlertTriangle,
  MessageSquare,
  X as XIcon,
} from "lucide-react";

import {
  listPermits,
  listPTWTemplates,
  permitWorkflowAction,
  resolveActiveProjectId,
  getUserGroups,
  getPermit,
  getPermitTbtAttendance,
  downloadPermitRegister,
  downloadPermitRegisterExcel,
  downloadPermitReport,
  downloadPermitTBTAttendanceReport,
} from "../../../../api";
import { resolveMediaUrl } from "../../../../lib/utils";
import { showToast } from "../../../../utils/toast";
import PermitSignatureModal from "../utils/PermitSignatureModal";
import PermitHeaderMeta from "../utils/PermitHeaderMeta";
import TbtAttendanceModal from "./TbtAttendanceModal";
import PermitReadonlyView from "../utils/PermitReadonlyView";
import PTWSignOffSection, { getClosureRemarksList } from "../utils/PTWSignOffSection";
import PermitQuestionDiscussion from "../utils/PermitQuestionDiscussion";
import {
  canShowPermitReportDownload,
  formatStatusLabel,
  isProvidedPpeField,
  getPermitWorkflowTimestamps,
  getPermitRejectionBadges,
  extractAnswerString,
  isSpecialCellDisabled,
  isTemperaturePermissibleLimitCell,
  getPermitMakerDisclaimer,
  getCheckerConsentConfig,
  filterPermitItem,
  buildContractorFilterOptions,
} from "../utils/permitHelpers";
import PermitFilters, { DEFAULT_PERMIT_FILTERS } from "./PermitFilters";
import PermitListMeta, {
  getPermitCardTitle,
  getPermitInternalTeamName,
} from "../utils/PermitListMeta";
import { getCurrentUserName } from "../../../../utils/UserUtils";
import { buildChecklistSections } from "../utils/permitChecklistSections";
import PermitChecklistSectionTabs from "../utils/PermitChecklistSectionTabs";
import ProvidedPpeBadges from "../utils/ProvidedPpeBadges";
import MultiImageUploadArea from "../utils/MultiImageUploadArea";
import PermitChecklistPhotosList from "../utils/PermitChecklistPhotosList";
import PermitDescriptionBox from "../utils/PermitDescriptionBox";
import SafetyImageAnnotationModal from "../../Safety_Inspection/SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../../Safety_Inspection/SafetyCameraCaptureModal";
import { SafetyHeroBanner } from "../../SafetyBannerArt";

const renderFieldValue = (val) => {
  if (val === null || val === undefined || val === "") return "N/A";
  if (Array.isArray(val)) {
    return val
      .map((item) => (typeof item === "object" ? renderFieldValue(item) : String(item)))
      .join(", ");
  }
  if (typeof val === "object") {
    const parts = Object.entries(val)
      .filter(([_, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => (typeof v === "object" ? `${k}: ${JSON.stringify(v)}` : String(v)));
    return parts.length > 0 ? parts.join(" - ") : "N/A";
  }
  return String(val);
};

const getQuestionImages = (permit, questionId) => {
  return (permit?.checklist_images || []).filter(
    (img) => String(img.question_id) === String(questionId),
  );
};

const getImageUrl = (img) => {
  return resolveMediaUrl(img?.file_url || img?.file || "");
};

const resolvePermitStatus = (permit) =>
  permit?.workflow_summary?.current_status ?? permit?.current_status;

const hasActionPermission = (permit) => {
  const permissions = permit?.workflow_permissions || {};

  return Boolean(
    permissions.can_approve ||
    permissions.can_reject ||
    permissions.can_hold ||
    permissions.can_stop
  );
};

const canReviewPermit = (permit) => {
  if (!permit) return false;

  if (permit.is_cancel_resubmission_review) return true;

  const status = resolvePermitStatus(permit);
  // An already issued, closed, cancelled or rejected permit is not awaiting checker review
  if (
    status === "issued" ||
    status === "closed" ||
    status === "rejected" ||
    status === "cancelled" ||
    status === "draft"
  ) {
    return false;
  }

  return hasActionPermission(permit);
};

const canResubmitPermit = (permit) => {
  if (!permit) return false;
  const isFinalRejected = Boolean(
    permit.is_final_rejected ||
    (permit.issue_rejection_count && Number(permit.issue_rejection_count) >= 3)
  );
  const isFinalCancelled = Boolean(
    permit.is_final_cancelled ||
    (permit.cancel_count && Number(permit.cancel_count) >= 3)
  );
  if (isFinalRejected || isFinalCancelled) return false;

  const status = resolvePermitStatus(permit);
  return Boolean(
    permit.workflow_permissions?.can_resubmit ||
    (permit.workflow_permissions?.can_submit && (status === "rejected" || status === "cancelled"))
  );
};

const canCancelPermit = (permit) => {
  if (!permit) return false;

  if (permit.workflow_permissions?.can_cancel !== undefined) {
    return Boolean(permit.workflow_permissions.can_cancel);
  }

  return false;
};

export const isCheckerActionablePermit = (permit) => {
  if (!permit) return false;
  const isReviewable = canReviewPermit(permit);
  const isResubmittable = canResubmitPermit(permit);
  const canRequestClosure = Boolean(permit?.workflow_permissions?.can_request_closure);
  return Boolean(isReviewable || isResubmittable || canRequestClosure);
};

export const sortPermitsActionableFirst = (list) => {
  if (!Array.isArray(list) || list.length <= 1) return list || [];
  const actionable = [];
  const viewOnly = [];
  list.forEach((item) => {
    if (isCheckerActionablePermit(item)) {
      actionable.push(item);
    } else {
      viewOnly.push(item);
    }
  });
  return [...actionable, ...viewOnly];
};

const getPermitPrimaryActionLabel = (permit) => {
  if (canReviewPermit(permit)) {
    if (permit?.is_cancel_resubmission_review) {
      return "Cancellation Review";
    }
    return "Review";
  }

  if (canResubmitPermit(permit)) {
    return "Resubmit";
  }

  return "View";
};
const formatPermitDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, "0");

  return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
};

const getPermitAssignedGroupName = (item, userGroups = []) => {
  if (!item?.current_assigned_group_id) return "";

  const name = (
    item.current_assigned_group_name ||
    item.workflow_summary?.current_group_name ||
    userGroups.find(
      (g) => Number(g.id) === Number(item.current_assigned_group_id),
    )?.name ||
    `Group ${item.current_assigned_group_id}`
  );
  if (String(name).trim().toLowerCase() === "internal") {
    return getPermitInternalTeamName(item);
  }
  return name;
};

export default function PermitCheckerDashboard({ activeWorkflowRole }) {
  const isMobileSession = localStorage.getItem("MOBILE_WEBVIEW_SESSION") === "true";
  const normalizedActiveRole = String(
    activeWorkflowRole ||
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ROLE") ||
    ""
  )
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

  const [projectId, setProjectId] = useState("");
  const [permits, setPermits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard"); // "dashboard" | "review" | "view"
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_PERMIT_FILTERS);
  const [remarks, setRemarks] = useState("");
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureActionTitle, setSignatureActionTitle] = useState("Approve");
  const [pendingAction, setPendingAction] = useState(null);
  const [tbtViewModalOpen, setTbtViewModalOpen] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({});
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [downloadingTbtReportId, setDownloadingTbtReportId] = useState(null);

  // NEW: optional photo attachment(s) for the closure decision step (checker side).
  // Purely additive — does not affect any existing state, UI, or workflow logic.
  const [closureImages, setClosureImages] = useState([]);

  // Checker checklist images state: { [questionId]: [File, ...] }
  const [checkerChecklistImages, setCheckerChecklistImages] = useState({});

  // Camera capture modal state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraTargetQuestionId, setCameraTargetQuestionId] = useState(null);

  // Image annotation modal state
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annotationFile, setAnnotationFile] = useState(null);

  // Cancellation Reason Modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [annotationTargetQuestionId, setAnnotationTargetQuestionId] = useState(null);
  const [annotationQueue, setAnnotationQueue] = useState([]);

  const [downloadingRegisterExcel, setDownloadingRegisterExcel] = useState(false);

  const handleDownloadRegister = async () => {
    try {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        projectId;
      await downloadPermitRegister(pid);
      showToast("Register downloaded successfully", "success");
    } catch (err) {
      let msg = err?.message || "Failed to download register";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json?.detail || json?.message || msg;
        } catch {}
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      showToast(msg, "error");
    }
  };

  const handleDownloadRegisterExcel = async () => {
    setDownloadingRegisterExcel(true);
    try {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        projectId;
      await downloadPermitRegisterExcel(pid);
      showToast("Excel register downloaded successfully", "success");
    } catch (err) {
      let msg = err?.message || "Failed to download Excel register";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json?.detail || json?.message || msg;
        } catch {}
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      showToast(msg, "error");
    } finally {
      setDownloadingRegisterExcel(false);
    }
  };

  const handleDownloadReport = async (permit) => {
    if (!permit?.id) return;
    setDownloadingReportId(permit.id);
    try {
      await downloadPermitReport(permit.id);
      showToast("Report downloaded successfully", "success");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to download permit report";
      showToast(message, "error");
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleDownloadTbtReport = async (permit) => {
    if (!permit?.id) return;
    setDownloadingTbtReportId(permit.id);
    try {
      await downloadPermitTBTAttendanceReport(permit.id);
      showToast("TBT Attendance Report downloaded successfully", "success");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to download TBT attendance report";
      showToast(message, "error");
    } finally {
      setDownloadingTbtReportId(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        localStorage.getItem("PROJECT_ID") ||
        localStorage.getItem("project_id") ||
        "";
      setProjectId(pid);
      const cleanPid = pid || undefined;

      const [
        allProjectPermitsRes,
        templatesRes,
      ] = await Promise.all([
        listPermits({
          project_id: cleanPid,
          refresh_workflow: true,
        }).catch(() => ({ data: [] })),
        listPTWTemplates().catch(() => ({ data: [] })),
      ]);

      const allProjectPermits = Array.isArray(allProjectPermitsRes?.data)
        ? allProjectPermitsRes.data
        : allProjectPermitsRes?.data?.results || [];

      let mergedPermits = [...allProjectPermits];

      // Fallback: If project-scoped queries returned no permits and no project was specified, try fetching visible permits globally
      if (mergedPermits.length === 0 && !cleanPid) {
        try {
          const globalVisibleRes = await listPermits({ visible_to_me: true, refresh_workflow: true }).catch(() => ({ data: [] }));
          const globalVisible = Array.isArray(globalVisibleRes?.data)
            ? globalVisibleRes.data
            : globalVisibleRes?.data?.results || [];
          mergedPermits = [...mergedPermits, ...globalVisible];
        } catch (_) {}
      }

      // Checkers should only see submitted/active permits, never unsubmitted drafts
      const validPermits = mergedPermits.filter(
        (permit) => String(permit?.current_status || "").toLowerCase() !== "draft"
      );

      const uniquePermits = Array.from(
        new Map(validPermits.map((permit) => [permit.id, permit])).values(),
      );

      setPermits(uniquePermits);
      setTemplates(
        Array.isArray(templatesRes?.data)
          ? templatesRes.data
          : templatesRes?.data?.results || [],
      );
    } catch (err) {
      showToast("Failed to load permits", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getUserGroups();
        const data = Array.isArray(res?.data)
          ? res.data
          : res?.data?.results || [];
        setUserGroups(data);
      } catch (err) {
        console.error("Failed to load user groups", err);
      }
    };
    fetchGroups();
  }, []);

  const [checklistRemarks, setChecklistRemarks] = useState({});

  useEffect(() => {
    setChecklistRemarks({});
    setReviewFormData(selectedPermit?.form_data || {});
    setClosureImages([]); // NEW: reset optional closure photos whenever a new permit is opened
    setCheckerChecklistImages({}); // Reset checker checklist images whenever a new permit is opened
  }, [selectedPermit]);

  const handleChecklistImagesChange = (questionId, files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) return;

    const imageFiles = selectedFiles.filter((file) =>
      String(file?.type || "").startsWith("image/"),
    );

    if (!imageFiles.length) {
      showToast("Please upload valid image files only.", "error");
      return;
    }

    if (imageFiles.length !== selectedFiles.length) {
      showToast("Some non-image files were skipped.", "warning");
    }

    setAnnotationTargetQuestionId(questionId);
    setAnnotationQueue(imageFiles);
    setAnnotationFile(imageFiles[0]);
    setAnnotationOpen(true);
  };

  const handleOpenChecklistCamera = (questionId) => {
    setCameraTargetQuestionId(questionId);
    setCameraOpen(true);
  };

  const handleChecklistCameraCapture = (capturedFile) => {
    const questionId = cameraTargetQuestionId;
    if (questionId === null || questionId === undefined || !capturedFile) {
      return;
    }

    if (!String(capturedFile?.type || "").startsWith("image/")) {
      showToast("Unable to capture a valid image.", "error");
      return;
    }

    setAnnotationTargetQuestionId(questionId);
    setAnnotationQueue([capturedFile]);
    setAnnotationFile(capturedFile);
    setAnnotationOpen(true);
  };

  const handleAnnotatedChecklistImageSave = (annotatedFile) => {
    if (
      annotatedFile &&
      annotationTargetQuestionId !== null &&
      annotationTargetQuestionId !== undefined
    ) {
      setCheckerChecklistImages((prev) => ({
        ...prev,
        [annotationTargetQuestionId]: [
          ...(prev[annotationTargetQuestionId] || []),
          annotatedFile,
        ],
      }));
    }

    setAnnotationQueue((prev) => {
      const nextQueue = prev.slice(1);
      if (nextQueue.length) {
        setAnnotationFile(nextQueue[0]);
        setAnnotationOpen(true);
      } else {
        setAnnotationOpen(false);
        setAnnotationFile(null);
        setAnnotationTargetQuestionId(null);
      }
      return nextQueue;
    });
  };

  const handleAnnotationClose = () => {
    setAnnotationOpen(false);
    setAnnotationFile(null);
    setAnnotationTargetQuestionId(null);
    setAnnotationQueue([]);
  };

  const removeChecklistImage = (questionId, index) => {
    setCheckerChecklistImages((prev) => {
      const current = prev[questionId] || [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...prev,
        [questionId]: next,
      };
    });
  };

  const handleOpenCancelModal = () => {
    setCancelReasonError("");
    setCancelModalOpen(true);
  };

  const handleConfirmCancel = () => {
    const trimmed = (cancelReason || "").trim();
    if (!trimmed) {
      setCancelReasonError("Cancellation reason is required.");
      showToast("Cancellation reason is required.", "error");
      return;
    }
    setCancelReasonError("");
    setCancelModalOpen(false);
    handleAction("cancel", null, null, trimmed);
  };

  const handleAction = async (actionType, signatureData = null, sigMeta = null, customRemarks = null) => {
    if (!selectedPermit) return;

    const currentUserName = getCurrentUserName();
    const isClosureMode =
      view === "closure_review" ||
      selectedPermit?.current_status === "closure_in_progress" ||
      selectedPermit?.workflow_summary?.current_status === "closure_in_progress" ||
      getStatus(selectedPermit) === "closure_in_progress";

    const isCancelMode = actionType === "cancel";
    const effectiveRemarks = isCancelMode
      ? (customRemarks !== null ? customRemarks : (remarks || cancelReason))
      : (customRemarks !== null ? customRemarks : remarks);

    const questionComments = Object.entries(checklistRemarks || {})
      .map(([questionId, comment]) => ({
        question_id: Number(questionId),
        comment: String(comment || "").trim(),
        source: isCancelMode
          ? "issued_cancel"
          : actionType === "reject"
            ? (isClosureMode ? "closure_reject" : "issue_reject")
            : (isClosureMode ? "closure_review" : "issue_review"),
        comment_type: "PUBLIC",
        user_name: currentUserName,
      }))
      .filter((item) => item.question_id && item.comment);

    if (isCancelMode && !String(effectiveRemarks || "").trim()) {
      showToast("Cancellation reason is required.", "error");
      return;
    }

    if (actionType === "reject" && !isClosureMode && questionComments.length === 0) {
      showToast("Please add a remark on at least one checklist point before rejecting.", "error");
      return;
    }

    if (isClosureMode && actionType === "reject") {
      if (!String(effectiveRemarks || "").trim()) {
        showToast("Overall remarks are required when rejecting closure.", "error");
        return;
      }
    }

    if (actionType === "approve") {
      const missingPhotoQuestion = (
        selectedPermit?.template_snapshot?.checklist_questions || []
      ).find((q) => {
        if (!q.photo_required && !q.requires_attachment) return false;
        const existingImgs = getQuestionImages(selectedPermit, q.id);
        const newImgs = checkerChecklistImages[q.id] || [];
        return existingImgs.length === 0 && newImgs.length === 0;
      });

      if (missingPhotoQuestion) {
        showToast(
          `Please attach a photo for required checklist point: ${missingPhotoQuestion.question}`,
          "error",
        );
        return;
      }
    }

    const actionLabel = isCancelMode
      ? "Cancel Permit"
      : actionType === "approve"
      ? "Approve"
      : actionType === "reject"
      ? "Reject"
      : "Submit Action";

    if ((actionType === "approve" || actionType === "reject" || isCancelMode) && !signatureData) {
      setSignatureActionTitle(actionLabel);
      setPendingAction(() => (sig, meta) => handleAction(actionType, sig, meta, effectiveRemarks));
      setSignatureModalOpen(true);
      return;
    }

    setIsSubmittingAction(true);
    try {
      // Switch to multipart/FormData when the checker has attached closure photos
      // OR checker checklist photos. Otherwise, send plain JSON payload.
      const hasCheckerChecklistImages = Object.values(checkerChecklistImages || {}).some(
        (files) => Array.isArray(files) && files.length > 0,
      );

      let payload;

      const cleanFormData =
        reviewFormData && typeof reviewFormData === "object"
          ? reviewFormData
          : {};

      let formattedChecklist = selectedPermit?.checklist_response || [];
      if (
        Array.isArray(formattedChecklist) &&
        formattedChecklist.length > 0 &&
        Array.isArray(formattedChecklist[0])
      ) {
        formattedChecklist = formattedChecklist.flat();
      }

      const finalRemarks = String(effectiveRemarks || "").trim();

      if (closureImages.length > 0 || hasCheckerChecklistImages) {
        payload = new FormData();
        payload.append("action", actionType);
        payload.append("remarks", finalRemarks);
        if (signatureData) payload.append("signature", signatureData);
        payload.append("form_data", JSON.stringify(cleanFormData));
        payload.append("actor_name", currentUserName);
        payload.append("user_name", currentUserName);

        if (sigMeta?.consent_accepted) {
          payload.append("consent_accepted", "true");
        }

        if (questionComments.length > 0) {
          payload.append("question_comments", JSON.stringify(questionComments));
        }

        if (Array.isArray(formattedChecklist) ? formattedChecklist.length > 0 : Boolean(formattedChecklist)) {
          payload.append(
            "checklist_response",
            JSON.stringify(formattedChecklist),
          );
        }

        closureImages.forEach((file) => {
          payload.append("closure_images", file);
        });

        Object.entries(checkerChecklistImages || {}).forEach(([questionId, files]) => {
          (files || []).forEach((file) => {
            payload.append(`checklist_images_${questionId}`, file);
          });
        });
      } else {
        payload = {
          action: actionType,
          remarks: finalRemarks,
          signature: signatureData,
          form_data: cleanFormData,
          actor_name: currentUserName,
          user_name: currentUserName,
        };

        if (sigMeta?.consent_accepted) {
          payload.consent_accepted = true;
        }

        if (questionComments.length > 0) {
          payload.question_comments = questionComments;
        }

        if (Array.isArray(formattedChecklist) ? formattedChecklist.length > 0 : Boolean(formattedChecklist)) {
          payload.checklist_response = formattedChecklist;
        }
      }

      await permitWorkflowAction(selectedPermit.id, payload);
      let successMsg = `Permit successfully ${isCancelMode ? "cancelled" : actionType === "approve" ? "approved" : actionType + "ed"}`;
      if (isClosureMode && actionType === "approve") {
        successMsg = "Permit Closure Approved Successfully";
      } else if (isClosureMode && actionType === "reject") {
        successMsg = "Permit closure rejected successfully";
      }
      showToast(successMsg, "success");
      setView("dashboard");
      setCancelReason("");
      setCancelReasonError("");
      setRemarks("");
      setSelectedGroupId("");
      setChecklistRemarks({});
      setClosureImages([]); // NEW: reset optional closure photos after a successful action
      setCheckerChecklistImages({}); // Reset checker checklist photos after a successful action
      fetchData();
    } catch (err) {
      const data = err?.response?.data;
      const detail =
        data?.question_comments ||
        data?.remarks ||
        data?.detail ||
        data?.message ||
        err?.message ||
        `Failed to ${actionType} permit`;

      const detailStr = typeof detail === "string" ? detail : JSON.stringify(detail);

      if (
        detailStr.toLowerCase().includes("signature") ||
        detailStr.toLowerCase().includes("consent")
      ) {
        setSignatureActionTitle(actionLabel);
        setPendingAction(() => (sig, meta) => handleAction(actionType, sig, meta, effectiveRemarks));
        setSignatureModalOpen(true);
      } else {
        showToast(detailStr || "Action failed", "error");
      }
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const getStatus = (p) =>
    p.workflow_summary?.current_status ?? p.current_status;

  const contractorOptions = useMemo(
    () => buildContractorFilterOptions(permits, [], projectId),
    [permits, projectId]
  );

  const filteredPermits = useMemo(() => {
    return permits.filter((p) => filterPermitItem(p, filters));
  }, [permits, filters]);

  const pendingApproval = useMemo(
    () =>
      sortPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "issue_in_progress")
      ),
    [filteredPermits]
  );
  const pendingClosure = useMemo(
    () =>
      sortPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "closure_in_progress")
      ),
    [filteredPermits]
  );
  const issuedPermits = useMemo(
    () =>
      sortPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "issued")
      ),
    [filteredPermits]
  );
  const closedPermits = useMemo(
    () =>
      sortPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "closed" && !p.is_final_rejected)
      ),
    [filteredPermits]
  );
  const rejectedPermits = useMemo(
    () =>
      sortPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "rejected" || p.is_final_rejected)
      ),
    [filteredPermits]
  );
  const cancelledPermits = useMemo(
    () =>
      sortPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "cancelled")
      ),
    [filteredPermits]
  );

  const handleReviewClick = async (item) => {
    try {
      const res = await getPermit(item.id, {
        project_id: item.project_id,
      });
      const detail = res?.data || res;
      setSelectedPermit(detail);
      if (canReviewPermit(detail)) {
        setView("review");
      } else {
        setView("view");
      }
    } catch (err) {
      showToast("Failed to load permit details", "error");
    }
  };

  const handleViewPermit = async (item) => {
    try {
      const res = await getPermit(item.id, {
        project_id: item.project_id,
      });
      const detail = res?.data || res;
      setSelectedPermit(detail);
      setView("view");
    } catch (err) {
      showToast("Failed to load permit details", "error");
    }
  };

  const openTbtViewModal = () => {
    if (!selectedPermit?.id) {
      showToast("Permit not found", "error");
      return;
    }
    setTbtViewModalOpen(true);
  };

  const renderPermitCard = (item, badgeClass) => (
    <div
      key={item.id}
      className="flex flex-col gap-2.5 border-b border-border/50 p-4 last:border-0"
    >
      {/* ROW 1: Title (left) & Status + Action Buttons (top right corner) */}
      <div className="flex items-start justify-between gap-2.5 sm:gap-3">
        <h3 className="min-w-0 flex-1 font-semibold text-foreground text-sm sm:text-base leading-snug break-words">
          {getPermitCardTitle(item)}
        </h3>

        {(() => {
          const isReviewable = canReviewPermit(item);
          const isResubmittable = canResubmitPermit(item);
          const canRequestClosure = Boolean(item.workflow_permissions?.can_request_closure);
          const hasWorkflowAction = isReviewable || isResubmittable || canRequestClosure;

          return (
            <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${badgeClass}`}
                >
                  {formatStatusLabel(getStatus(item))}
                </span>

                {hasWorkflowAction ? (
                  <>
                    {isReviewable && (
                      <button
                        type="button"
                        onClick={() => handleReviewClick(item)}
                        className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md whitespace-nowrap shrink-0 cursor-pointer"
                      >
                        {item?.is_cancel_resubmission_review ? "Cancellation Review" : "Review"}
                      </button>
                    )}
                    {isResubmittable && (
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href = `/safety/permit-to-work/create?permitId=${item.id}`;
                        }}
                        className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md whitespace-nowrap shrink-0 cursor-pointer"
                      >
                        Resubmit
                      </button>
                    )}
                    {canRequestClosure && (
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href = `/safety/permit-to-work/create?permitId=${item.id}`;
                        }}
                        className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md whitespace-nowrap shrink-0 cursor-pointer"
                      >
                        Request Closure
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleViewPermit(item)}
                    className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 whitespace-nowrap shrink-0 cursor-pointer"
                  >
                    View
                  </button>
                )}
              </div>

              {hasWorkflowAction && (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => handleViewPermit(item)}
                    className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 whitespace-nowrap shrink-0 cursor-pointer"
                  >
                    View
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ROW 2: Report / Attendance Download Actions (if applicable) */}
      {canShowPermitReportDownload(item) && (
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pt-0.5">
          <button
            type="button"
            onClick={() => handleDownloadReport(item)}
            disabled={downloadingReportId === item.id}
            className="rounded-lg bg-green-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {downloadingReportId === item.id
              ? "Downloading..."
              : "Download Permit Report"}
          </button>

          <button
            type="button"
            onClick={() => handleDownloadTbtReport(item)}
            disabled={downloadingTbtReportId === item.id}
            className="rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {downloadingTbtReportId === item.id
              ? "Downloading..."
              : "Download TBT Attendance"}
          </button>
        </div>
      )}

      {/* ROW 3: Timestamps & Waiting on */}
      <div className="text-xs text-muted-foreground">
        {(() => {
          const ts = getPermitWorkflowTimestamps(item);
          return (
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 gap-y-0.5 font-normal">
              {ts.startTimeText && (
                <span>{ts.startTimeText}</span>
              )}
              {ts.endTimeText && (
                <span>
                  {ts.startTimeText ? "· " : ""}{ts.endTimeText}
                </span>
              )}
              {item.current_assigned_group_id && (
                <span className="text-slate-600">
                  {ts.startTimeText || ts.endTimeText ? "· " : ""}Waiting on: {getPermitAssignedGroupName(item, userGroups)}
                </span>
              )}
              {ts.mode === "closed" && ts.closedTimeDisplay && (
                <span className="text-slate-500">
                  {ts.startTimeText || ts.endTimeText || item.current_assigned_group_id ? "· " : ""}Closed: {ts.closedTimeDisplay}
                </span>
              )}
            </div>
          );
        })()}
      </div>
      {getStatus(item) === "cancelled" && (
        <div className="flex flex-col gap-0.5">
          {(item.latest_cancelled_by_name || item.latest_cancel_reason) && (
            <p className="text-xs text-red-600 font-medium">
              Cancelled by {item.latest_cancelled_by_name || "Checker"}
              {item.latest_cancel_reason ? `: "${item.latest_cancel_reason}"` : ""}
              {item.latest_cancelled_at_display ? ` on ${item.latest_cancelled_at_display}` : ""}
            </p>
          )}
          <p className="text-xs font-semibold text-red-600">
            Cancellation {item.cancel_count || 1} of 3
            {item.is_final_cancelled || (item.cancel_count || 0) >= 3 ? " · Limit Reached (Final Cancelled)" : ""}
          </p>
        </div>
      )}
      {getStatus(item) === "rejected" && (
        <div className="flex flex-wrap items-center gap-2">
          {item.closure_rejection_count > 0 ? (
            <p className="text-xs font-semibold text-amber-600">
              Closure Rejection {item.closure_rejection_count} of 3
            </p>
          ) : (
            <p className="text-xs font-semibold text-orange-600">
              Approval Rejection {item.issue_rejection_count || 1} of 3
              {item.is_final_rejected || (item.issue_rejection_count || 0) >= 3 ? " · Limit Reached (Final Rejected)" : ""}
            </p>
          )}
        </div>
      )}
      {getStatus(item) !== "rejected" && getStatus(item) !== "cancelled" && (
        <div className="flex flex-wrap items-center gap-2">
          {item.issue_rejection_count > 0 && (
            <span className="text-xs font-medium text-orange-600">
              Approval Rejection {item.issue_rejection_count} of 3
            </span>
          )}
          {item.closure_rejection_count > 0 && (
            <span className="text-xs font-medium text-amber-600">
              Closure Rejection {item.closure_rejection_count} of 3
            </span>
          )}
          {item.cancel_count > 0 && (
            <span className="text-xs font-medium text-red-600">
              Cancellation {item.cancel_count} of 3
            </span>
          )}
        </div>
      )}

      {/* ROW 4: Full-width Metadata Badges */}
      <PermitListMeta item={item} projectId={projectId} />
    </div>
  );

  const renderPermitBucket = (title, items, icon, colorClass, badgeClass) => {
    const actionableItems = [];
    const viewOnlyItems = [];
    (items || []).forEach((item) => {
      if (isCheckerActionablePermit(item)) {
        actionableItems.push(item);
      } else {
        viewOnlyItems.push(item);
      }
    });

    const hasActionable = actionableItems.length > 0;
    const hasViewOnly = viewOnlyItems.length > 0;

    return (
      <div className="flex flex-col">
        <div className="mb-2 flex items-center gap-2">
          {icon}
          <h2
            className="text-sm font-semibold text-foreground sm:text-base"
            style={{ color: colorClass }}
          >
            {title}
          </h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {(items || []).length}
          </span>
        </div>

        <div className="h-[480px] overflow-y-auto rounded-xl border bg-card shadow-sm">
          {!items || items.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted-foreground">No items</p>
            </div>
          ) : (
            <>
              {hasActionable && (
                <div data-testid="group-action-required">
                  <div className="sticky top-0 z-10 flex items-center justify-between border-b border-amber-200/80 bg-amber-50/95 px-4 py-1.5 backdrop-blur-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
                        Action Required
                      </span>
                    </div>
                    <span className="rounded-full bg-amber-200/80 px-2 py-0.5 text-[10px] font-extrabold text-amber-900">
                      {actionableItems.length}
                    </span>
                  </div>
                  {actionableItems.map((item) => renderPermitCard(item, badgeClass))}
                </div>
              )}

              {hasViewOnly && (
                <div data-testid="group-view-only">
                  <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-slate-100/95 px-4 py-1.5 backdrop-blur-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                        View Only
                      </span>
                    </div>
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-extrabold text-slate-700">
                      {viewOnlyItems.length}
                    </span>
                  </div>
                  {viewOnlyItems.map((item) => renderPermitCard(item, badgeClass))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

const getCurrentPermitGroup = () => {
  const groupId =
        selectedPermit?.workflow_summary?.current_group_id ??
        selectedPermit?.current_assigned_group_id;

        const groupCode =
        selectedPermit?.workflow_summary?.current_group_code ??
        selectedPermit?.current_assigned_group_code ??
        "";

        const groupNameFromPermit =
        selectedPermit?.workflow_summary?.current_group_name ??
        selectedPermit?.current_assigned_group_name ??
        "";

        const groupFromList = userGroups.find(
    (g) => String(g.id) === String(groupId),
        );

        return {
          id: groupId,
        code: groupCode || groupFromList?.code || "",
        name: groupNameFromPermit || groupFromList?.name || "",
  };
};

const canCurrentCheckerEditColumn = (col) => {
  if (!col?.editable) return false;

        const editableBy = col.editable_by || [];
        if (!editableBy.includes("checker")) return false;

        const colKey = String(col.key || "").trim();

        if (colKey === "carried_out_by_contractor_ehs") {
    if (typeof col.can_current_user_edit === "boolean") {
      return col.can_current_user_edit;
    }

        const effectiveCodes =
        selectedPermit?.effective_issue_flow_group_codes ||
        selectedPermit?.workflow_summary?.effective_issue_flow_group_codes ||
        [];

    const contractorEhsInFlow = (effectiveCodes || []).map(c => String(c).toUpperCase()).includes("CONTRACTOR_EHS");

        if (!contractorEhsInFlow) {
      const currentGroup = getCurrentPermitGroup();
        return String(currentGroup.code || "").trim().toUpperCase() === "ADL_EHS";
    }
  }

        if (typeof col.can_current_user_edit === "boolean") {
    return col.can_current_user_edit;
  }

        const currentGroup = getCurrentPermitGroup();

        // Prefer group id match
        if (col.checker_group_id) {
    return String(currentGroup.id) === String(col.checker_group_id);
  }

        // Fallback to group code
        if (col.checker_group_code) {
    return (
        String(currentGroup.code || "")
        .trim()
        .toLowerCase() ===
        String(col.checker_group_code || "")
        .trim()
        .toLowerCase()
        );
  }

        // Fallback to group name
        if (col.checker_group_name) {
    return (
        String(currentGroup.name || "")
        .trim()
        .toLowerCase() ===
        String(col.checker_group_name || "")
        .trim()
        .toLowerCase()
        );
  }

        // If no group restriction is configured, allow checker edit.
        return true;
};

        if (isMobileSession && normalizedActiveRole !== "CHECKER") {
  return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
          <div className="max-w-md rounded-xl bg-white p-8 shadow-sm border border-slate-200">
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              Unauthorized Access
            </h2>
            <p className="text-sm text-slate-600 mb-6">
              Your active mobile role ({normalizedActiveRole}) is not authorized to access the Permit Checker dashboard.
            </p>
            <button
              type="button"
              onClick={() => (window.location.href = "/config")}
              className="rounded-lg bg-orange-500 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-600 transition-colors"
            >
              Return to Workspace Config
            </button>
          </div>
        </div>
        );
}

        return (
        <div className="min-h-screen bg-slate-50/60 p-4 sm:p-6 lg:p-8">
          {view === "dashboard" && (
            <div className="mx-auto w-full max-w-[1600px] space-y-6">
              <SafetyHeroBanner
                type="permit"
                categoryTag="PERMIT TO WORK SYSTEM"
                categoryIcon={ShieldCheck}
                title="Permit To Work"
                subtitle="Safety permit management dashboard — authorize, monitor, and sign off site permits."
              >
                <button
                  type="button"
                  onClick={handleDownloadRegisterExcel}
                  disabled={downloadingRegisterExcel}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white/90 backdrop-blur-sm px-4 py-2.5 text-xs sm:text-sm font-semibold text-emerald-700 transition-all hover:bg-emerald-50 hover:shadow-sm disabled:opacity-50 cursor-pointer shadow-xs"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  {downloadingRegisterExcel ? "Downloading..." : "Download Register"}
                </button>
              </SafetyHeroBanner>

              <PermitFilters
                filters={filters}
                setFilters={setFilters}
                templates={templates}
                contractors={contractorOptions}
                showDraftOption={false}
              />

              {loading ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : (
                <>
                  {/* Counter cards */}
                  <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
                    {[
                      {
                        key: "issue_in_progress",
                        label: "Pending for Approval",
                        icon: <Clock className="h-3.5 w-3.5" />,
                        colorCls: "hsl(45, 93%, 47%)",
                        count: pendingApproval.length,
                      },
                      {
                        key: "closure_in_progress",
                        label: "Pending for Closer",
                        icon: <Send className="h-3.5 w-3.5" />,
                        colorCls: "hsl(24.6, 95%, 53.1%)",
                        count: pendingClosure.length,
                      },
                      {
                        key: "issued",
                        label: "Issued",
                        icon: <CheckCircle className="h-3.5 w-3.5" />,
                        colorCls: "hsl(145, 65%, 42%)",
                        count: issuedPermits.length,
                      },
                      {
                        key: "closed",
                        label: "Closed",
                        icon: <CheckCircle className="h-3.5 w-3.5" />,
                        colorCls: "hsl(200, 70%, 45%)",
                        count: closedPermits.length,
                      },
                      {
                        key: "rejected",
                        label: "Rejected",
                        icon: <CircleAlert className="h-3.5 w-3.5" />,
                        colorCls: "hsl(0, 75%, 55%)",
                        count: rejectedPermits.length,
                      },
                      {
                        key: "cancelled",
                        label: "Cancelled",
                        icon: <XCircle className="h-3.5 w-3.5" />,
                        colorCls: "hsl(220, 9%, 46%)",
                        count: cancelledPermits.length,
                      },
                      {
                        key: "all",
                        label: "Total",
                        icon: <ClipboardList className="h-3.5 w-3.5" />,
                        colorCls: "hsl(263, 83%, 53%)",
                        count: filteredPermits.length,
                      },
                    ].map(({ key, label, icon, colorCls, count }) => {
                      const isSelected = filters.status === key;
                      return (
                        <div
                          key={label}
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              status: prev.status === key ? "all" : key,
                            }));
                          }}
                          className={`group relative flex flex-col justify-between h-full rounded-2xl border bg-card p-3.5 sm:p-4 shadow-2xs transition-all duration-200 cursor-pointer select-none hover:-translate-y-0.5 hover:shadow-md ${
                            isSelected
                              ? "ring-2 ring-offset-1 border-transparent shadow-sm"
                              : "border-slate-200/90 hover:border-slate-300"
                          }`}
                          style={{
                            borderColor: isSelected ? colorCls : undefined,
                            boxShadow: isSelected ? `0 0 0 2px ${colorCls}40` : undefined,
                          }}
                          title={`Filter by ${label}`}
                        >
                          {/* Top Row: Icon Badge + Label with fixed min-height for uniform alignment */}
                          <div className="flex items-start gap-2 min-h-[38px]">
                            <span
                              className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors"
                              style={{
                                backgroundColor: `${colorCls}18`,
                                color: colorCls,
                              }}
                            >
                              {icon}
                            </span>
                            <span
                              className="text-xs sm:text-[13px] font-semibold leading-snug line-clamp-2"
                              style={{ color: colorCls }}
                            >
                              {label}
                            </span>
                          </div>

                          {/* Bottom Row: Count strictly aligned to horizontal baseline */}
                          <div className="mt-3 flex items-baseline border-t border-border/40 pt-2.5">
                            <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                              {Number.isFinite(Number(count)) ? Number(count) : 0}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bucket Lists */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {renderPermitBucket(
                      "Pending for Approval",
                      pendingApproval,
                      <Clock className="h-4 w-4 text-muted-foreground" />,
                      "hsl(45, 93%, 47%)",
                      "bg-yellow-100 text-yellow-800",
                    )}

                    {renderPermitBucket(
                      "Pending for Closer",
                      pendingClosure,
                      <Send className="h-4 w-4 text-muted-foreground" />,
                      "hsl(24.6, 95%, 53.1%)",
                      "bg-orange-100 text-orange-800",
                    )}

                    {renderPermitBucket(
                      "Issued Permits",
                      issuedPermits,
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />,
                      "hsl(145, 65%, 42%)",
                      "bg-green-100 text-green-800",
                    )}

                    {renderPermitBucket(
                      "Closed Permits",
                      closedPermits,
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />,
                      "hsl(200, 70%, 45%)",
                      "bg-blue-100 text-blue-800",
                    )}

                    {renderPermitBucket(
                      "Rejected Permits",
                      rejectedPermits,
                      <CircleAlert className="h-4 w-4 text-muted-foreground" />,
                      "hsl(0, 75%, 55%)",
                      "bg-red-100 text-red-800",
                    )}

                    {renderPermitBucket(
                      "Cancelled Permits",
                      cancelledPermits,
                      <XCircle className="h-4 w-4 text-muted-foreground" />,
                      "hsl(220, 9%, 46%)",
                      "bg-slate-100 text-slate-700",
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {view === "review" && selectedPermit && (
            <div className="mx-auto w-full max-w-6xl 2xl:max-w-7xl">
              <button
                onClick={() => {
                  setView("dashboard");
                  setSelectedPermit(null);
                }}
                className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </button>

              <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                    <ClipboardCheck className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-foreground sm:text-xl">
                      {selectedPermit.template_snapshot?.template_name ||
                        selectedPermit.template_name ||
                        `Permit #${selectedPermit.id}`}
                    </h1>
                  </div>
                </div>
              </div>

              <PermitHeaderMeta
                headerConfig={selectedPermit.template_snapshot?.header_config}
                formatNo={selectedPermit.template_snapshot?.format_no}
                refNo={selectedPermit.template_snapshot?.ref_no}
                issuedDateText={selectedPermit.template_snapshot?.issued_date_text}
                revisionNo={selectedPermit.template_snapshot?.revision_no}
                projectId={selectedPermit.project_id}
                permitId={selectedPermit.id}
                ptwNo={selectedPermit.ptw_no}
                permit={selectedPermit}
              />
              <div className="space-y-6">
                <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between shadow-sm">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-700">
                        Current Status:{" "}
                        <span className="text-orange-600">
                          {formatStatusLabel(
                            selectedPermit.workflow_summary?.current_status ??
                            selectedPermit.current_status
                          )}
                        </span>
                      </p>
                      {getPermitRejectionBadges(selectedPermit).map((badge, idx) => (
                        <span
                          key={`${badge.type}-${idx}`}
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.label}
                          {badge.isLimitReached ? " · Limit Reached" : ""}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-slate-600">
                      Pending Group:{" "}
                      <span className="font-medium text-slate-800">
                        {selectedPermit.workflow_summary?.current_group_name ??
                          userGroups.find(
                            (g) =>
                              g.id === selectedPermit.current_assigned_group_id,
                          )?.name ??
                          `Group ${selectedPermit.current_assigned_group_id}`}
                      </span>
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <p>
                      Submitted:{" "}
                      {new Date(
                        selectedPermit.submitted_at || selectedPermit.created_at,
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Banner when viewing an already issued permit in review view */}
                {getStatus(selectedPermit) === "issued" && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-emerald-950">
                          Permit is Active & Issued
                        </p>
                        <p className="text-xs text-emerald-700">
                          Work is currently underway by the Permit Holder. Approval or rejection will become active when the maker requests closure.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setView("view")}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-emerald-700 whitespace-nowrap shrink-0"
                    >
                      Open Full View
                    </button>
                  </div>
                )}

                {/* Closure Resubmission Alert Banner */}
                {/* {(() => {
                  const isClosureStage =
                    view === "closure_review" ||
                    selectedPermit?.current_status === "closure_in_progress";
                  if (!isClosureStage || Number(selectedPermit?.closure_rejection_count || 0) === 0) return null;
                  const remarksList = getClosureRemarksList(selectedPermit);
                  const latestMakerResubmit = [...remarksList].reverse().find(
                    (r) => r.isClosureRequest && r.roleLabel?.includes("Resubmission")
                  );
                  if (!latestMakerResubmit) return null;
                  return (
                    <div className="rounded-xl border border-blue-200 bg-blue-50/80 p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 text-sm font-bold text-blue-900">
                          <MessageSquare className="h-4 w-4 text-blue-600" />
                          <span>Maker Closure Resubmission Remark</span>
                        </div>
                        <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-bold text-blue-800 border border-blue-200">
                          Closure Rejection {selectedPermit.closure_rejection_count} Resubmission
                        </span>
                      </div>
                      <p className="text-xs text-blue-950 font-medium whitespace-pre-wrap leading-relaxed">
                        {latestMakerResubmit.remark}
                      </p>
                      {latestMakerResubmit.author && (
                        <p className="mt-1.5 text-[11px] text-blue-600">
                          Submitted by <span className="font-semibold">{latestMakerResubmit.author}</span>
                          {latestMakerResubmit.date ? ` on ${latestMakerResubmit.date}` : ""}
                        </p>
                      )}
                    </div>
                  );
                })()} */}

                {/* SECTION 1: Permit Details */}
                {selectedPermit?.template_snapshot?.field_definitions?.length >
                  0 && (
                    <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                      <h3 className="mb-4 text-md font-semibold text-slate-800">
                        Permit Details
                      </h3>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {selectedPermit.template_snapshot.field_definitions.map(
                          (field) => {
                            const value = selectedPermit.form_data?.[field.key];
                            const isPpe = isProvidedPpeField(field);

                            return (
                              <div
                                key={field.key}
                                className={`rounded-lg bg-slate-50 p-3 border border-slate-100 ${isPpe ? "sm:col-span-2" : ""
                                  }`}
                              >
                                <p className="text-xs font-semibold text-slate-500">
                                  {field.label}
                                </p>
                                {isPpe ? (
                                  <ProvidedPpeBadges value={value} />
                                ) : (
                                  <p className="mt-1 text-sm text-slate-900 font-medium">
                                    {renderFieldValue(value)}
                                  </p>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </section>
                  )}

                {/* SECTION 2: Permit Description */}
                <PermitDescriptionBox
                  description={
                    selectedPermit?.template_snapshot?.description ||
                    selectedPermit?.template?.description
                  }
                  title="Permit Description"
                  titleClassName="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500"
                />

                {/* SECTION 3: Checklist & Review */}
                {selectedPermit?.template_snapshot?.checklist_questions?.length >
                  0 && (() => {
                    const checkerSectionGroups = buildChecklistSections({
                      sections: selectedPermit.template_snapshot.checklist_sections || [],
                      questions: selectedPermit.template_snapshot.checklist_questions || [],
                    });
                    const firstQId = selectedPermit.template_snapshot.checklist_questions?.[0]?.id;

                    const getCheckerBadgeInfo = (group) => {
                      const commentedCount = group.questions.filter((q) => {
                        const resp = selectedPermit?.checklist_response?.find(
                          (r) => String(r.question_id ?? r.id) === String(q.id)
                        );
                        if (resp?.remarks && String(resp.remarks).trim()) return true;
                        const qLogs = selectedPermit?.question_logs?.[String(q.id)];
                        const comments = Array.isArray(qLogs?.comments) ? qLogs.comments : [];
                        return comments.some((c) => {
                          const src = String(c.source || "").toLowerCase();
                          const isMaker = src.includes("maker") || src === "resubmit";
                          const isCreator = selectedPermit?.created_by_id && c.user_id && Number(c.user_id) === Number(selectedPermit.created_by_id);
                          return (isMaker || isCreator) && Boolean(String(c.comment || "").trim());
                        });
                      }).length;

                      if (commentedCount > 0) {
                        return {
                          label: `${commentedCount} commented`,
                          colorCls: "bg-amber-500 text-white font-bold",
                        };
                      }
                      return null;
                    };

                    return (
                      <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                        <h3 className="mb-4 text-md font-semibold text-slate-800">
                          Checklist Points
                        </h3>
                        <PermitChecklistSectionTabs
                          sectionGroups={checkerSectionGroups}
                          getBadgeInfo={getCheckerBadgeInfo}
                        >
                          {(currentGroup) => (
                            <div className="flex flex-col gap-4">
                              {currentGroup.questions.map(
                                (q, idx) => {
                                  const response = selectedPermit.checklist_response?.find(
                                    (r) => String(r.question_id ?? r.id) === String(q.id),
                                  );
                                  const answer = extractAnswerString(response?.answer) || "No answer";
                                  const images = getQuestionImages(selectedPermit, q.id);
                                  const isFirstQuestionOverall = q.id === firstQId;
                                  const qNumber = idx + 1;

                                  const makerComment =
                                    (response?.remarks && String(response.remarks).trim()) ||
                                    (() => {
                                      const qLogs = selectedPermit?.question_logs?.[String(q.id)];
                                      const comments = Array.isArray(qLogs?.comments) ? qLogs.comments : [];
                                      const found = [...comments].reverse().find((c) => {
                                        const src = String(c.source || "").toLowerCase();
                                        const isMaker = src.includes("maker") || src === "resubmit";
                                        const isCreator = selectedPermit?.created_by_id && c.user_id && Number(c.user_id) === Number(selectedPermit.created_by_id);
                                        return (isMaker || isCreator) && Boolean(String(c.comment || "").trim());
                                      });
                                      return found?.comment ? String(found.comment).trim() : "";
                                    })();

                                  const hasMakerComment = Boolean(makerComment);
                                  const normAns = String(answer || "").trim().toLowerCase();

                                  return (
                                    <div
                                      key={q.id}
                                      className={`rounded-xl border p-5 shadow-sm mb-4 transition-all ${
                                        hasMakerComment
                                          ? "border-amber-300 bg-amber-50/70 ring-1 ring-amber-200"
                                          : "border-slate-200 bg-card"
                                      }`}
                                    >
                                      <div className="mb-4 flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3">
                                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
                                            {qNumber}
                                          </span>

                                          <div className="mt-1 flex-1">
                                            <p className="text-[15px] font-semibold text-slate-800">
                                              {q.question}
                                            </p>
                                          </div>
                                        </div>

                                        <div className="flex shrink-0 items-center gap-2">
                                          {isFirstQuestionOverall && (
                                            <button
                                              type="button"
                                              onClick={openTbtViewModal}
                                              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                                            >
                                              TBT Attendance
                                            </button>
                                          )}

                                          {hasMakerComment && (
                                            <span className="rounded-full bg-amber-100 border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-800 flex items-center gap-1 shadow-2xs">
                                              <span>💬</span> Maker Commented
                                            </span>
                                          )}

                                          <span
                                            className={`rounded-full px-3 py-1 text-xs font-bold ${
                                              normAns === "yes"
                                                ? "bg-green-100 text-green-700"
                                                : normAns === "no"
                                                  ? "bg-red-100 text-red-700"
                                                  : normAns === "n/a" || normAns === "na"
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : "bg-slate-100 text-slate-700"
                                            }`}
                                          >
                                            {answer}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="ml-0 sm:ml-11">
                                        {images.length > 0 && (
                                          <PermitChecklistPhotosList images={images} />
                                        )}
                                        {hasMakerComment && (
                                          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-100/70 p-3 text-xs shadow-2xs">
                                            <p className="font-bold text-amber-900 flex items-center gap-1.5">
                                              <span>💬 Maker Comment:</span>
                                            </p>
                                            <p className="mt-1 text-amber-950 font-medium whitespace-pre-wrap">{makerComment}</p>
                                          </div>
                                        )}

                                        {(canReviewPermit(selectedPermit) || canCancelPermit(selectedPermit)) && (
                                          <MultiImageUploadArea
                                            questionId={q.id}
                                            files={checkerChecklistImages[q.id] || []}
                                            label={
                                              q.photo_required || q.requires_attachment
                                                ? "Attach review photo (Required)"
                                                : "Attach review photo (Optional)"
                                            }
                                            onGalleryChange={handleChecklistImagesChange}
                                            onTakePhoto={handleOpenChecklistCamera}
                                            onRemove={removeChecklistImage}
                                          />
                                        )}

                                        <PermitQuestionDiscussion
                                          logs={selectedPermit?.question_logs?.[String(q.id)]}
                                        />

                                        {(canReviewPermit(selectedPermit) || canCancelPermit(selectedPermit)) && (
                                          <div className="mt-3">
                                            <label className="mb-1 block text-xs font-semibold text-slate-700">
                                              Checker Remark
                                            </label>
                                            <textarea
                                              value={checklistRemarks[q.id] || ""}
                                              onChange={(e) =>
                                                setChecklistRemarks((prev) => ({
                                                  ...prev,
                                                  [q.id]: e.target.value,
                                                }))
                                              }
                                              placeholder="Add your remark for this checklist point (Optional)..."
                                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-colors"
                                              rows={2}
                                            />
                                          </div>
                                        )}

                                        {!canReviewPermit(selectedPermit) && !canCancelPermit(selectedPermit) && view === "review" && (
                                          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-400">
                                            Remarks can be added only by eligible checker groups.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          )}
                        </PermitChecklistSectionTabs>
                      </section>
                    );
                  })()}

                {/* SECTION 4: Special Sections */}
                {(() => {
                  const specialSections =
                    selectedPermit?.resolved_special_sections ||
                    selectedPermit?.template_snapshot?.special_sections ||
                    [];

                  if (!specialSections.length) return null;

                  return (
                    <section className="space-y-6">
                      {specialSections.map((section) => (
                        <div key={section.key} className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                          <h3 className="mb-4 text-md font-semibold text-slate-800">
                            {section.title}
                          </h3>

                          {section.type === "row_table" ? (
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse border border-slate-300 text-sm">
                                <thead>
                                  <tr className="bg-slate-100">
                                    <th className="border border-slate-300 p-2 text-left w-12">
                                      S.No
                                    </th>
                                    {section.columns?.map((col) => (
                                      <th
                                        key={col.key}
                                        className="border border-slate-300 p-2 text-left"
                                      >
                                        {col.label}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {section.rows?.map((row, idx) => (
                                    <tr key={row.key}>
                                      <td className="border border-slate-300 p-2">
                                        {idx + 1}
                                      </td>
                                      {section.columns?.map((col) => {
                                        if (
                                          isSpecialCellDisabled(
                                            row,
                                            col,
                                            section,
                                            selectedPermit?.form_data
                                          )
                                        ) {
                                          return (
                                            <td
                                              key={col.key}
                                              className="border border-slate-300 p-2 text-slate-600 bg-slate-50/50"
                                            >
                                              {""}
                                            </td>
                                          );
                                        }

                                        const checkerCanEdit =
                                          canCurrentCheckerEditColumn(col);

                                        const currentValue =
                                          reviewFormData?.[section.key]?.rows?.[
                                          row.key
                                          ]?.[col.key] ??
                                          selectedPermit.form_data?.[
                                            section.key
                                          ]?.rows?.[row.key]?.[col.key] ??
                                          row[col.key] ??
                                          "";

                                        if (checkerCanEdit) {
                                          return (
                                            <td
                                              key={col.key}
                                              className="border border-slate-300 p-2"
                                            >
                                              <input
                                                type={
                                                  col.field_type === "number" || col.type === "number"
                                                    ? "number"
                                                    : "text"
                                                }
                                                value={currentValue}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  setReviewFormData((prev) => ({
                                                    ...prev,
                                                    [section.key]: {
                                                      ...(prev[section.key] || {}),
                                                      rows: {
                                                        ...(prev[section.key]?.rows || {}),
                                                        [row.key]: {
                                                          ...(prev[section.key]?.rows?.[row.key] || {}),
                                                          [col.key]: val,
                                                        },
                                                      },
                                                    },
                                                  }));
                                                }}
                                                className="w-full rounded border border-slate-300 bg-white p-1.5 text-sm text-slate-800 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                                placeholder={`Enter ${col.label.toLowerCase()}...`}
                                              />
                                            </td>
                                          );
                                        }

                                        return (
                                          <td
                                            key={col.key}
                                            className="border border-slate-300 p-2 text-slate-800"
                                          >
                                            {renderFieldValue(currentValue)}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {/* Footer Fields (e.g. Signatures, Dates inside row_table) */}
                              {section.footer_fields?.length > 0 && (
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 border-t border-slate-200 pt-4">
                                  {section.footer_fields.map((ff) => {
                                    const val =
                                      reviewFormData?.[section.key]?.[ff.key] ??
                                      selectedPermit.form_data?.[section.key]?.[
                                      ff.key
                                      ] ??
                                      "";

                                    return (
                                      <div
                                        key={ff.key}
                                        className="rounded-lg bg-slate-50 p-3 border border-slate-100"
                                      >
                                        <p className="text-xs font-medium text-slate-500">
                                          {ff.label}
                                        </p>
                                        <p className="mt-1 text-sm text-slate-900 font-medium">
                                          {renderFieldValue(val)}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ) : section.type === "table" ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {section.fields?.map((f) => {
                                const val =
                                  selectedPermit.form_data?.[section.key]?.[
                                  f.key
                                  ] ?? "";
                                return (
                                  <div
                                    key={f.key}
                                    className="rounded-lg bg-slate-50 p-3 border border-slate-100"
                                  >
                                    <p className="text-xs font-medium text-slate-500">
                                      {f.label}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-900 font-medium">
                                      {renderFieldValue(val)}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">
                              Unsupported special section type: {section.type}
                            </p>
                          )}
                        </div>
                      ))}
                    </section>
                  );
                })()}

                {/* SECTION 5: Sign-Off & Review Decision */}
                <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                  <div className="mb-6">
                    <PTWSignOffSection permit={selectedPermit} />
                  </div>

                  {/* Additional Safety Precaution / Remarks :- (Configured group in issue flow only) */}
                  {(() => {
                    const additionalSafety = selectedPermit?.additional_safety_precaution;
                    const isIssueMode = selectedPermit?.current_status === "issue_in_progress";
                    const targetGroupCode = additionalSafety?.editable_by_group_code || "ADL_EHS";
                    const canEditAdditionalSafety =
                      isIssueMode &&
                      additionalSafety?.enabled &&
                      (additionalSafety?.can_current_user_edit ||
                        selectedPermit?.current_assigned_group_code === targetGroupCode);

                    if (!canEditAdditionalSafety) return null;

                    return (
                      <div className="mb-6">
                        <label className="mb-2 block text-sm font-semibold text-slate-800">
                          {additionalSafety?.label || "Additional Safety Precaution / Remarks :-"}
                        </label>
                        <textarea
                          value={reviewFormData.additional_safety_precaution_remarks || ""}
                          onChange={(e) =>
                            setReviewFormData((prev) => ({
                              ...prev,
                              additional_safety_precaution_remarks: e.target.value,
                            }))
                          }
                          placeholder="Enter additional safety precaution or remarks (optional)..."
                          className="w-full rounded-lg border bg-white p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-shadow"
                          rows={3}
                        />
                      </div>
                    );
                  })()}

                  {/* Overall Remarks :- (Closure flow only) */}
                  {(view === "closure_review" || selectedPermit?.current_status === "closure_in_progress") && (
                    <div className="mb-6">
                      <h3 className="mb-4 text-lg font-semibold text-slate-800">
                        Closure Decision
                      </h3>

                      {/* Maker Closure Request Remark & Prior Remarks */}
                      {(() => {
                        const existingClosureRemarks = getClosureRemarksList(selectedPermit);
                        if (!existingClosureRemarks.length) return null;

                        return (
                          <div className="mb-4 space-y-2">
                            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">
                              Maker Closure Request & Prior Remarks
                            </label>
                            <div className="space-y-2">
                              {existingClosureRemarks.map((item, idx) => (
                                <div
                                  key={item.id || idx}
                                  className="rounded-xl border border-blue-200 bg-blue-50/70 p-3.5 shadow-2xs"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-blue-950">
                                        {item.author}
                                      </span>
                                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold border ${item.badgeColor}`}>
                                        {item.roleLabel}
                                      </span>
                                    </div>
                                    {item.date && (
                                      <span className="text-[11px] text-blue-600 font-medium">
                                        {item.date}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-blue-900 whitespace-pre-wrap leading-relaxed font-medium">
                                    {item.remark}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Overall Remarks <span className="text-slate-400 font-normal">(Required for Rejection)</span>
                      </label>
                      <textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Enter overall closure remarks (required when rejecting)..."
                        className="w-full rounded-lg border bg-white p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 transition-shadow"
                        rows={3}
                      />

                      {/* NEW: optional photo attachment during closure decision. Purely additive UI —
                    does not replace or alter the Overall Remarks field or any existing logic. */}
                      <div className="mt-4">
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Attach Photo <span className="text-slate-400 font-normal">(Optional)</span>
                        </label>

                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length === 0) return;
                            setClosureImages((prev) => [...prev, ...files]);
                            e.target.value = ""; // allow re-selecting the same file later
                          }}
                          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-orange-50 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-orange-700 hover:file:bg-orange-100"
                        />

                        {closureImages.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-3">
                            {closureImages.map((file, idx) => (
                              <div
                                key={`${file.name}-${idx}`}
                                className="relative h-20 w-20 overflow-hidden rounded-lg border bg-white shadow-sm"
                              >
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Closure attachment ${idx + 1}`}
                                  className="h-full w-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setClosureImages((prev) => prev.filter((_, i) => i !== idx))
                                  }
                                  className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                                  title="Remove"
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Maker Declaration & Disclaimer Box */}
                  <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50/80 p-4 shadow-xs">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                          <span className="text-xs font-bold uppercase tracking-wide text-orange-950">
                            Applicant / Maker Declaration & Disclaimer
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-orange-200/80 px-2.5 py-0.5 text-[10px] font-bold text-orange-950">
                            ✓ Confirmed & Accepted by Maker
                          </span>
                        </div>
                        <p className="text-xs text-slate-700 leading-relaxed font-medium">
                          {getPermitMakerDisclaimer(selectedPermit)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col justify-end gap-3 border-t border-border/50 pt-6 sm:flex-row">
                    {canCancelPermit(selectedPermit) && (
                      <button
                        type="button"
                        onClick={handleOpenCancelModal}
                        disabled={isSubmittingAction}
                        className="flex w-full items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 transition-all hover:bg-red-100 sm:w-auto cursor-pointer disabled:opacity-50"
                      >
                        Cancel Permit
                      </button>
                    )}

                    {selectedPermit.workflow_permissions?.can_reject && (
                      <button
                        onClick={() => handleAction("reject")}
                        className="flex w-full items-center justify-center rounded-xl bg-red-500 px-4 py-3 text-sm font-bold text-white transition-all hover:bg-red-600 sm:w-auto"
                      >
                        Reject
                      </button>
                    )}

                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                      {selectedPermit.workflow_permissions?.can_approve && (
                        <button
                          onClick={() => handleAction("approve")}
                          className="flex w-full items-center justify-center rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-green-700 sm:w-auto"
                        >
                          Approve
                        </button>
                      )}
                    </div>

                    {!selectedPermit.workflow_permissions?.can_approve &&
                      !selectedPermit.workflow_permissions?.can_reject &&
                      !canCancelPermit(selectedPermit) && (
                        <div className="flex items-center justify-between w-full sm:w-auto gap-3">
                          <span className="text-xs text-slate-500 font-medium">
                            No active review actions available for current status ({formatStatusLabel(getStatus(selectedPermit))}).
                          </span>
                          <button
                            type="button"
                            onClick={() => setView("view")}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            Switch to View Mode
                          </button>
                        </div>
                      )}
                  </div>
                </section>
              </div>
            </div>
          )}

          {view === "view" && selectedPermit && (
            <PermitReadonlyView
              permit={selectedPermit}
              userType="checker"
              titlePrefix="View Permit"
              onBack={() => {
                setView("dashboard");
                setSelectedPermit(null);
              }}
              onCancelPermit={(item) => {
                setSelectedPermit(item);
                handleOpenCancelModal();
              }}
            />
          )}

          <TbtAttendanceModal
            isOpen={tbtViewModalOpen}
            onClose={() => setTbtViewModalOpen(false)}
            permit={selectedPermit}
            projectId={selectedPermit?.project_id || projectId || resolveActiveProjectId?.()}
            mode="api"
            readonly={false}
          />

          {/* Signature Modal */}
          <PermitSignatureModal
            isOpen={signatureModalOpen}
            actionTitle={signatureActionTitle}
            onClose={() => {
              setSignatureModalOpen(false);
              setPendingAction(null);
            }}
            onSignatureSuccess={(sigData, sigMeta) => {
              setSignatureModalOpen(false);
              if (pendingAction) pendingAction(sigData, sigMeta);
            }}
            consentConfig={getCheckerConsentConfig(selectedPermit, userGroups)}
          />

          {/* Camera Capture Modal */}
          <SafetyCameraCaptureModal
            open={cameraOpen}
            title="Take Permit Review Photo"
            onClose={() => setCameraOpen(false)}
            onCapture={handleChecklistCameraCapture}
          />

          {/* Image Annotation Modal */}
          <SafetyImageAnnotationModal
            open={annotationOpen}
            file={annotationFile}
            onClose={handleAnnotationClose}
            onSave={handleAnnotatedChecklistImageSave}
          />

          {/* Cancellation Reason Modal */}
          {cancelModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-150">
              <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200 animate-in zoom-in-95 duration-150">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 bg-slate-50/50">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-100 text-red-600">
                      <XCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">
                        Cancel Permit
                      </h3>
                      <p className="text-xs text-slate-500 font-medium">
                        {selectedPermit?.ptw_no || selectedPermit?.template?.name || `Permit #${selectedPermit?.id}`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCancelReasonError("");
                      setCancelModalOpen(false);
                    }}
                    disabled={isSubmittingAction}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                  {/* Warning Callout */}
                  <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-900 leading-relaxed flex items-start gap-2.5">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold">Warning:</span> Are you sure you want to cancel this permit?
                      This action will cancel the active permit and the cancellation reason will be visible to the Permit Maker and other authorized Checkers.
                    </div>
                  </div>

                  {/* Question Feedback Status Callout */}
                  {(() => {
                    const activeQuestionRemarksCount = Object.values(checklistRemarks || {}).filter(
                      (txt) => String(txt || "").trim().length > 0
                    ).length;

                    if (activeQuestionRemarksCount > 0) {
                      return (
                        <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-3 text-xs text-blue-900 leading-relaxed flex items-start gap-2.5">
                          <MessageSquare className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold">{activeQuestionRemarksCount} checklist question(s) flagged with feedback.</span> Only these flagged questions will become editable for the Maker during resubmission.
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 text-xs text-slate-700 leading-relaxed flex items-start gap-2.5">
                        <CheckCircle className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                        <div>
                          <span className="font-bold">No individual checklist questions flagged.</span> The Maker will be permitted to edit and correct all checklist questions before resubmitting.
                        </div>
                      </div>
                    );
                  })()}

                  {/* Textarea */}
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-slate-700">
                      Cancellation Reason / Remarks <span className="text-red-500 font-bold">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={cancelReason}
                      onChange={(e) => {
                        setCancelReason(e.target.value);
                        if (cancelReasonError) setCancelReasonError("");
                      }}
                      disabled={isSubmittingAction}
                      placeholder="Enter the reason for cancelling this permit (e.g. Unsafe work area, adverse weather, emergency stop)..."
                      className={`w-full rounded-xl border p-3 text-xs outline-none transition-all placeholder:text-slate-400 ${
                        cancelReasonError
                          ? "border-red-400 bg-red-50/40 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                          : "border-slate-300 bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100"
                      }`}
                    />
                    {cancelReasonError && (
                      <p className="mt-1 text-[11px] font-semibold text-red-600">
                        {cancelReasonError}
                      </p>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                  <button
                    type="button"
                    onClick={() => {
                      setCancelReasonError("");
                      setCancelModalOpen(false);
                    }}
                    disabled={isSubmittingAction}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmCancel}
                    disabled={isSubmittingAction}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-5 py-2.5 text-xs font-bold text-white shadow-md hover:bg-red-700 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmittingAction ? "Processing..." : "Proceed to Sign & Cancel"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        );
}